/* eslint-disable react-refresh/only-export-components -- page exports a small
   helper (relativeTime) alongside the default page component. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import KpiCard from './components/KpiCard';
import SalesChart, { type SalesPoint } from './components/SalesChart';
import PaymentSplit, { type PaymentSlice } from './components/PaymentSplit';
import TopItems, { type TopItem } from './components/TopItems';
import LiveOrders from './components/LiveOrders';
import AlertsPanel, { type DashboardAlert } from './components/AlertsPanel';
import { useSession } from '@/lib/session';
import { useDashboardDate } from '@/lib/dashboardDate';
import {
  fetchInventory,
  fetchOrderItemsBetween,
  fetchOrders,
  subscribeOrders,
  type InventoryItem,
  type Order,
  type OrderItem,
} from '@/lib/data';

const DAY_MS = 86_400_000;
const ACTIVE_STATUSES = ['received', 'preparing', 'ready'];

type WeekOrderItem = OrderItem & { orders: { placedAt: string } };

function sumKpis(orders: Order[]) {
  const valid = orders.filter((o) => o.status !== 'cancelled');
  const revenue = valid.reduce((s, o) => s + o.total, 0);
  const count = valid.length;
  return {
    revenue,
    count,
    aov: count > 0 ? Math.round(revenue / count) : 0,
    itemsSold: valid.reduce((s, o) => s + o.itemCount, 0),
    tax: valid.reduce((s, o) => s + o.cgst + o.sgst, 0),
  };
}

/** % change vs yesterday; null when there is no baseline. */
function delta(today: number, yesterday: number): number | null {
  if (yesterday === 0) return null;
  return Math.round(((today - yesterday) / yesterday) * 1000) / 10;
}

export function relativeTime(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function OverviewPage() {
  const { user, restaurant } = useSession();
  const { label, from, to } = useDashboardDate();
  const fromMs = from.getTime();
  const toMs = to.getTime();
  const [weekOrders, setWeekOrders] = useState<Order[]>([]);
  const [weekItems, setWeekItems] = useState<WeekOrderItem[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    // Fetch from the start of the previous comparable period (for the % deltas)
    // up to now (so Live Orders always reflect current active orders). Order
    // items are fetched for the selected range only (Top Items follows it).
    const prevFrom = new Date(fromMs - (toMs - fromMs));
    try {
      const [orders, items, inv] = await Promise.all([
        fetchOrders(restaurant.id, { from: prevFrom.toISOString() }),
        fetchOrderItemsBetween(restaurant.id, new Date(fromMs).toISOString(), new Date(toMs).toISOString()),
        fetchInventory(restaurant.id),
      ]);
      setWeekOrders(orders);
      setWeekItems(items);
      setInventory(inv);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [restaurant.id, fromMs, toMs]);

  useEffect(() => {
    void load();
    const unsubscribe = subscribeOrders(restaurant.id, () => { void load(); });
    return unsubscribe;
  }, [restaurant.id, load]);

  const computed = useMemo(() => {
    // Selected range + the equivalent preceding period (for the deltas).
    const periodLen = toMs - fromMs;
    const prevFrom = fromMs - periodLen;
    const rangeOrders = weekOrders.filter((o) => {
      const t = new Date(o.placedAt).getTime();
      return t >= fromMs && t < toMs;
    });
    const prevOrders = weekOrders.filter((o) => {
      const t = new Date(o.placedAt).getTime();
      return t >= prevFrom && t < fromMs;
    });

    const today = sumKpis(rangeOrders);
    const yesterday = sumKpis(prevOrders);

    // Sales trend: daily totals across the SELECTED range, split by order type.
    // Capped at the most recent 31 day-buckets so a huge custom range stays
    // readable.
    const bucketDayStart = new Date(fromMs);
    bucketDayStart.setHours(0, 0, 0, 0);
    let nDays = Math.max(1, Math.ceil((toMs - bucketDayStart.getTime()) / DAY_MS));
    let firstBucket = bucketDayStart.getTime();
    if (nDays > 31) { firstBucket = bucketDayStart.getTime() + (nDays - 31) * DAY_MS; nDays = 31; }
    const manyDays = nDays > 8;
    const salesTrend: SalesPoint[] = Array.from({ length: nDays }, (_, i) => {
      const dayStart = firstBucket + i * DAY_MS;
      const d = new Date(dayStart);
      const point: SalesPoint = {
        day: manyDays
          ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
          : d.toLocaleDateString('en-IN', { weekday: 'short' }),
        dineIn: 0,
        takeaway: 0,
        aggregator: 0,
      };
      for (const o of weekOrders) {
        if (o.status === 'cancelled') continue;
        const t = new Date(o.placedAt).getTime();
        if (t < dayStart || t >= dayStart + DAY_MS) continue;
        const type = (o.type ?? '').toLowerCase();
        if (type.includes('dine')) point.dineIn += o.total;
        else if (type.includes('take')) point.takeaway += o.total;
        else point.aggregator += o.total;
      }
      return point;
    });

    // Payment split: selected range's non-cancelled orders grouped by paymentMode.
    const byMode = new Map<string, number>();
    for (const o of rangeOrders) {
      if (o.status === 'cancelled') continue;
      const mode = o.paymentMode || 'Other';
      byMode.set(mode, (byMode.get(mode) ?? 0) + o.total);
    }
    const paymentTotal = [...byMode.values()].reduce((s, v) => s + v, 0);
    const paymentSplit: PaymentSlice[] = [...byMode.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([method, amount]) => ({
        method,
        amount,
        percentage: paymentTotal > 0 ? Math.round((amount / paymentTotal) * 100) : 0,
      }));

    // Top items: order items across the selected range, grouped by name, top 5
    // by qty. Trend compares the recent vs earlier half of the range.
    const midpoint = (fromMs + toMs) / 2;
    const byName = new Map<string, { qty: number; revenue: number; recent: number; earlier: number }>();
    for (const it of weekItems) {
      const agg = byName.get(it.name) ?? { qty: 0, revenue: 0, recent: 0, earlier: 0 };
      agg.qty += it.qty;
      agg.revenue += it.qty * it.price;
      if (new Date(it.orders.placedAt).getTime() >= midpoint) agg.recent += it.qty;
      else agg.earlier += it.qty;
      byName.set(it.name, agg);
    }
    const topItems: TopItem[] = [...byName.entries()]
      .sort((a, b) => b[1].qty - a[1].qty)
      .slice(0, 5)
      .map(([name, a]) => ({
        name,
        orders: a.qty,
        revenue: a.revenue,
        trend: a.recent > a.earlier ? 'up' : a.recent < a.earlier ? 'down' : 'stable',
      }));

    // Live orders: active statuses, newest first (fetch is already desc).
    const liveOrders = weekOrders.filter((o) => ACTIVE_STATUSES.includes(o.status));

    // Alerts: low stock + payments pending today.
    const alerts: DashboardAlert[] = [
      ...inventory
        .filter((i) => i.currentStock <= i.reorderPoint)
        .map((i): DashboardAlert => ({
          id: `stock-${i.id}`,
          message: `${i.name} running low — ${i.currentStock} ${i.unit} left`,
          severity: i.currentStock <= 0 ? 'critical' : 'warning',
          time: `Reorder at ${i.reorderPoint} ${i.unit}`,
        })),
      ...rangeOrders
        .filter((o) => o.status !== 'cancelled' && o.paymentStatus === 'pending')
        .map((o): DashboardAlert => ({
          id: `pay-${o.id}`,
          message: `Payment pending for order #${o.id.slice(0, 8)} — ₹${o.total.toLocaleString('en-IN')}`,
          severity: 'info',
          time: relativeTime(o.placedAt),
        })),
    ];

    return { today, yesterday, salesTrend, paymentSplit, topItems, liveOrders, alerts };
  }, [weekOrders, weekItems, inventory, fromMs, toMs]);

  const firstName = (user.displayName ?? '').split(/\s+/)[0];

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-lg font-bold text-foreground-800">Dashboard</h2>
        <p className="text-sm text-foreground-500 mt-0.5">
          Welcome back{firstName ? `, ${firstName}` : ''}. Here is what is happening at {restaurant.name}
          {label === 'Today' ? ' today' : ` · ${label}`}.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-600">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
          <button onClick={() => { setLoading(true); void load(); }} className="ml-auto font-medium text-primary-500 hover:text-primary-600 cursor-pointer">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-24 bg-background-50 border border-background-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="lg:col-span-2 h-72 bg-background-50 border border-background-200 rounded-lg animate-pulse"></div>
            <div className="h-72 bg-background-50 border border-background-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-64 bg-background-50 border border-background-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            <KpiCard label="Net Sales" value={computed.today.revenue} change={delta(computed.today.revenue, computed.yesterday.revenue)} />
            <KpiCard label="Total Orders" value={computed.today.count} change={delta(computed.today.count, computed.yesterday.count)} isCurrency={false} />
            <KpiCard label="Avg. Order Value" value={computed.today.aov} change={delta(computed.today.aov, computed.yesterday.aov)} />
            <KpiCard label="Items Sold" value={computed.today.itemsSold} change={delta(computed.today.itemsSold, computed.yesterday.itemsSold)} isCurrency={false} />
            <KpiCard label="Tax Collected" value={Math.round(computed.today.tax)} change={delta(computed.today.tax, computed.yesterday.tax)} />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="lg:col-span-2">
              <SalesChart data={computed.salesTrend} />
            </div>
            <PaymentSplit data={computed.paymentSplit} />
          </div>

          {/* Bottom row: Top Items + Live Orders + Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            <TopItems items={computed.topItems} />
            <LiveOrders orders={computed.liveOrders} />
            <AlertsPanel alerts={computed.alerts} />
          </div>
        </>
      )}
    </div>
  );
}
